import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';

import { provideRouter } from '@angular/router';

// Msal Imports
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  RedirectRequest,
  AuthenticationResult,
} from '@azure/msal-browser';
import { PromptValue } from '@azure/msal-browser';

import {
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  // MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalGuardConfiguration,
  MsalInterceptor,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/interceptor/auth.interceptor';
import { timeoutInterceptor } from './core/interceptor/timeout.interceptor';
import { DatePipe } from '@angular/common';
import { diagnosticInterceptor } from './core/diagnostics/diagnostic.interceptor';
import { DiagnosticSessionService } from './core/diagnostics/diagnostic-session.service';

export const PASSWORD_RESET_SUCCESS_KEY = 'passwordResetCompleted';

export const b2cPolicies = {
  names: {
    signUpSignIn: environment.signUpSignIn,
    resetPassword: environment.resetPassword,
  },
  authorities: {
    signUpSignIn: {
      authority: `${environment.authority}/${environment.signUpSignIn}`,
    },
    resetPassword: {
      authority: `${environment.authority}/${environment.resetPassword}`,
    },
  },
  authorityDomain: environment.authority,
};

export const apiConfig: { b2cScopes: string[]; webApi: string } = {
  b2cScopes: [
    `${environment.tenant}/${environment.clientId}/user_impersonation`,
    `${environment.tenant}/${environment.clientId}/read`,
    `${environment.tenant}/${environment.clientId}/write`,
  ],
  webApi: `${environment.frontBaseURL}/portal/home`,
};

export const MSALInstanceFactory = (): IPublicClientApplication =>
  new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      // The main redirect flow returns to the application root, as registered
      // in B2C. The application initializer processes the response there.
      redirectUri: environment.frontBaseURL,
      postLogoutRedirectUri: environment.frontBaseURL,
      knownAuthorities: [b2cPolicies.authorityDomain],
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
    },
  });

export const MSALInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  // MSAL Angular v5 uses strict URL matching. The wildcard protects every
  // endpoint below the API base URL.
  protectedResourceMap.set(`${environment.baseURL}/*`, [...apiConfig.b2cScopes]);

  // The Power BI token endpoint is handled by the legacy auth interceptor.
  // Direct calls to api.powerbi.com remain excluded there.
  protectedResourceMap.set(environment.tokenPowerBI, null);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
};

export const MSALGuardConfigFactory = (): MsalGuardConfiguration => {
  const config: MsalGuardConfiguration = {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.b2cScopes],
    },
    loginFailedRoute: '/',
  };

  return config;
};

export async function initializeMsal(
  msalInstance: IPublicClientApplication,
  msalBroadcastService: MsalBroadcastService,
): Promise<void> {
  await msalInstance.initialize();

  let redirectError: unknown;
  let redirectResult: AuthenticationResult | null = null;
  try {
    redirectResult = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: true });
  } catch (error) {
    redirectError = error;
  } finally {
    // handleRedirectPromise is invoked directly during bootstrap, so notify
    // MsalBroadcastService that the initial interaction has finished.
    msalBroadcastService.resetInProgressEvent();
  }

  const message = redirectError instanceof Error ? redirectError.message : String(redirectError ?? '');

  // The password-reset policy returns a valid token, but that token is not the
  // token used by the application API. Start the sign-in policy immediately.
  // This must happen here because AppComponent is created after the initializer
  // and would otherwise miss the initial LOGIN_SUCCESS event.
  if (redirectResult) {
    const claims = redirectResult.idTokenClaims as { acr?: string; tfp?: string };
    const policy = (claims.acr ?? claims.tfp ?? '').toLowerCase();

    if (policy === environment.resetPassword.toLowerCase()) {
      // Keep the success state for AppComponent. The sign-in policy is
      // started only after the user acknowledges the success modal.
      sessionStorage.setItem(PASSWORD_RESET_SUCCESS_KEY, 'true');
      await msalInstance.clearCache({ account: redirectResult.account });
      return;
    }

    if (policy === environment.signUpSignIn.toLowerCase()) {
      msalInstance.setActiveAccount(redirectResult.account);
    }
  }

  if (message.includes('AADB2C90118')) {
    await msalInstance.loginRedirect({
      authority: `${environment.authority}/${environment.resetPassword}`,
      scopes: [...apiConfig.b2cScopes],
    } as RedirectRequest);
    return;
  }

  if (message.includes('AADB2C90091')) {
    await msalInstance.loginRedirect({
      authority: `${environment.authority}/${environment.signUpSignIn}`,
      scopes: [...apiConfig.b2cScopes],
      prompt: PromptValue.LOGIN,
    } as RedirectRequest);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // authInterceptor is scoped to the legacy Power BI APIM endpoint;
    // MsalInterceptor continues to own the normal API authentication flow.
    provideHttpClient(
      withInterceptors([authInterceptor, timeoutInterceptor, diagnosticInterceptor]),
      withInterceptorsFromDi(),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalBroadcastService,
    MsalGuard,
    DatePipe,
    MsalService,
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    provideAppInitializer(() => initializeMsal(inject(MSAL_INSTANCE), inject(MsalBroadcastService))),
    provideAppInitializer(() => inject(DiagnosticSessionService).initialize()),
  ],
};
